// assets/product-custom-icons.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomIconsRefs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} searchLabel - The search label element.
 * @property {HTMLElement} noResults - The no results message element.
 * @property {NodeList} icons - The icon elements.
 * @property {HTMLElement} orderIconContainer - The order list container.
 * @property {HTMLElement} orderHeading - The order section heading.
 * @property {HTMLElement} propertiesContainer - The properties container.
 */

/**
 * A custom element that manages product custom icons selection
 * @extends Component<ProductCustomIconsRefs>
 */
class ProductCustomIcons extends Component {
    constructor() {
        super();
        this.formId = this.dataset.formId;
        this.limit = parseInt(this.dataset.limit) || 4;
        this.form = document.getElementById(this.formId);
        this.buyButton = this.form?.querySelector('button[type="submit"]') ||
            this.form?.querySelector('.add-to-cart-text--added') ||
            this.form?.querySelector('buy-buttons button');
        this.available = !this.buyButton?.disabled;
        this._el = null;
    }

    get refs() {
        return {
            searchInput: this.querySelector('[ref="searchInput"]'),
            searchLabel: this.querySelector('[ref="searchLabel"]'),
            noResults: this.querySelector('[ref="noResults"]'),
            icons: this.querySelector('[ref="icons"]'),
            orderIconContainer: this.querySelector('[ref="orderIconContainer"]'),
            orderHeading: this.querySelector('[ref="orderHeading"]'),
            propertiesContainer: this.querySelector('[ref="propertiesContainer"]'),
        };
    }

    get icons() {
        const iconsContainer = this.refs.icons;
        if (!iconsContainer) {
            return [];
        }
        return iconsContainer.querySelectorAll('.product-custom-icon');
    }

    connectedCallback() {
        super.connectedCallback();
        this.#setupEventListeners();
        this.checkATCAvailable();
    }

    #setupEventListeners() {
        // Add click listeners to all icon elements
        if (this.icons) {
            this.icons.forEach((icon) => {
                icon.addEventListener('click', this.onClickIcon.bind(this));
            });
        }

        if (this.refs.searchInput) {
            this.refs.searchInput.addEventListener('input', this.onSearch.bind(this));
        }

        if (this.form) {
            this.form.addEventListener('variant:change', this.onChangeVariant.bind(this));
        }
    }

    onClickIcon(e) {
        const icon = e.currentTarget;

        if (icon.classList.contains('disabled')) {
            return;
        }

        // Check if we've already reached the limit
        const currentCount = this.refs.orderIconContainer.querySelectorAll('.product-custom-icons__order-icon').length;
        if (currentCount >= this.limit) {
            return;
        }

        const { title, handle, tags } = icon.dataset;
        const imgHTML = icon.querySelector('img')?.outerHTML || '';

        const orderIcon = document.createElement('li');
        orderIcon.draggable = true;
        orderIcon.className = 'product-custom-icons__order-icon';
        orderIcon.dataset.title = title;
        orderIcon.dataset.handle = handle;
        orderIcon.dataset.tags = tags;
        orderIcon.innerHTML = `
      <div>
        ${imgHTML}
      </div>
      <span class="product-custom-icons__order-icon-remove">x</span>
    `;

        orderIcon.addEventListener('dragover', this.onDragOver.bind(this));
        orderIcon.addEventListener('dragstart', this.onDragStart.bind(this));

        orderIcon.querySelector('.product-custom-icons__order-icon-remove').addEventListener('click', () => {
            orderIcon.remove();
            this.initProperties();
        });

        this.refs.orderIconContainer.appendChild(orderIcon);
        this.initProperties();
    }

    onSearch(e) {
        e.preventDefault();
        const q = this.refs.searchInput.value.trim().toLowerCase();
        const queryHandle = this.handleize(q);
        const hasQuery = q.length > 0;
        let count = 0;

        this.icons.forEach(icon => {
            const title = icon.dataset.title?.toLowerCase() || '';
            const handle = icon.dataset.handle?.toLowerCase() || '';
            let tags = [];

            try {
                tags = JSON.parse(icon.dataset.tags || '{tags: []}').tags;
            } catch (err) {
                // Invalid JSON in data-tags, skip
            }

            const tagMatches = tags.some(tag => {
                const t = tag.toLowerCase();
                return t.includes(q) || t.includes(queryHandle);
            });

            const shouldShow =
                !hasQuery ||
                title.includes(q) ||
                handle.includes(queryHandle) ||
                tagMatches;

            icon.classList.toggle('hidden', !shouldShow);
            if (shouldShow) count++;
        });

        this.refs.noResults.classList.toggle('hidden', count > 0);
    }

    handleize(str) {
        return str
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-');
    }

    initProperties() {
        this.checkATCAvailable();
        if (this.refs.propertiesContainer) {
            this.refs.propertiesContainer.innerHTML = '';

            // Remove any existing icon properties from the form
            const existingIconInputs = this.form.querySelectorAll('input[name^="properties[icon"]');
            existingIconInputs.forEach(input => input.remove());

            const orderIcons = this.refs.orderIconContainer.querySelectorAll('.product-custom-icons__order-icon');
            orderIcons.forEach((icon, index) => {
                // Create input element directly and append to form
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = `properties[icon${index + 1}]`;
                input.value = icon.dataset.title;

                // Append directly to the form element
                this.form.appendChild(input);
            });

            if (this.refs.orderHeading) {
                this.refs.orderHeading.classList.toggle('hidden', !orderIcons.length);
            }

            if (this.refs.searchLabel) {
                const newText = this.refs.searchLabel.dataset.label.replace('_COUNT_', `${orderIcons.length}/${this.limit}`);
                this.refs.searchLabel.textContent = newText;
            }
        }
    }

    onChangeVariant(e) {
        const currentVariant = e.detail.variant;
        this.available = currentVariant.available;
        this.checkATCAvailable();
    }

    checkATCAvailable() {
        if (!this.refs.orderIconContainer) return;

        const orderIcons = this.refs.orderIconContainer.querySelectorAll('.product-custom-icons__order-icon');
        const hasReachedLimit = orderIcons.length >= this.limit;

        // Try to find the button if not found yet
        if (!this.buyButton) {
            this.buyButton = this.form?.querySelector('button[type="submit"]') ||
                this.form?.querySelector('.add-to-cart-text--added') ||
                this.form?.querySelector('buy-buttons button') ||
                this.form?.querySelector('button');
        }

        if (this.buyButton) {
            if (hasReachedLimit && this.available) {
                this.buyButton.disabled = false;
                this.buyButton.classList.remove('button--outline');
                this.icons.forEach(icon => {
                    icon.classList.add('disabled');
                });
            } else {
                this.buyButton.disabled = true;
                this.buyButton.classList.add('button--outline');
                this.icons.forEach(icon => {
                    icon.classList.remove('disabled');
                });
            }
        }
    }

    onDragOver(e) {
        if (this.isBefore(this._el, e.currentTarget))
            e.currentTarget.parentNode.insertBefore(this._el, e.currentTarget);
        else
            e.currentTarget.parentNode.insertBefore(this._el, e.currentTarget.nextSibling);

        this.initProperties();
    }

    onDragStart(e) {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", null);
        this._el = e.currentTarget;
    }

    isBefore(el1, el2) {
        if (el2.parentNode === el1.parentNode)
            for (var cur = el1.previousSibling; cur && cur.nodeType !== 9; cur = cur.previousSibling)
                if (cur === el2)
                    return true;
        return false;
    }
}

customElements.define('product-custom-icons', ProductCustomIcons);